import React, { useState, useEffect } from 'react';
import { X, Plus, Minus, Trash2, ShoppingBag, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from './ui/sheet';
import { Separator } from './ui/separator';
import { ScrollArea } from './ui/scroll-area';

export const Cart = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const [cartItems, setCartItems] = useState([]);

  // Load cart from localStorage
  useEffect(() => {
    const savedCart = localStorage.getItem('burbujasCart');
    if (savedCart) {
      setCartItems(JSON.parse(savedCart));
    }
  }, [isOpen]);

  // Save cart to localStorage
  useEffect(() => {
    localStorage.setItem('burbujasCart', JSON.stringify(cartItems));
    // Dispatch custom event to update cart count
    window.dispatchEvent(new CustomEvent('cartUpdate', { detail: cartItems.length }));
  }, [cartItems]);

  const updateQuantity = (cartItemId, change) => {
    setCartItems(items =>
      items.map(item =>
        item.cartItemId === cartItemId
          ? { ...item, quantity: Math.max(1, item.quantity + change) }
          : item
      )
    );
  };

  const removeItem = (cartItemId) => {
    setCartItems(items => items.filter(item => item.cartItemId !== cartItemId));
  };

  const clearCart = () => {
    setCartItems([]);
  };

  const subtotal = cartItems.reduce(
    (sum, item) => sum + item.selectedVariant.price * item.quantity,
    0
  );

  const shipping = 200; // Envío fijo
  const total = subtotal + shipping;

  const handleCheckout = () => {
    onClose();
    navigate('/checkout');
  };

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent className="w-full sm:max-w-lg flex flex-col">
        <SheetHeader>
          <SheetTitle className="flex items-center text-2xl text-honey-950">
            <ShoppingBag className="w-6 h-6 mr-2 text-honey-600" />
            Tu carrito
          </SheetTitle>
          <SheetDescription>
            {cartItems.length === 0
              ? 'Tu carrito está vacío'
              : `${cartItems.length} ${cartItems.length === 1 ? 'producto' : 'productos'} en tu carrito`}
          </SheetDescription>
        </SheetHeader>

        {cartItems.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center py-12">
            <ShoppingBag className="w-20 h-20 text-honey-300 mb-4" />
            <p className="text-honey-900/70 text-center mb-6">
              Aún no has agregado productos a tu carrito
            </p>
            <Button
              onClick={onClose}
              className="bg-honey-600 hover:bg-honey-700 text-white"
            >
              Explorar productos
            </Button>
          </div>
        ) : (
          <>
            <ScrollArea className="flex-1 -mx-6 px-6 py-4">
              <div className="space-y-4">
                {cartItems.map((item) => (
                  <div
                    key={item.cartItemId}
                    className="flex gap-4 p-4 border border-honey-100 rounded-lg hover:border-honey-300 transition-colors"
                  >
                    <img
                      src={item.image}
                      alt={item.name}
                      className="w-20 h-20 object-cover rounded-md flex-shrink-0"
                    />
                    <div className="flex-1 min-w-0">
                      <h4 className="font-semibold text-honey-950 mb-1 truncate">
                        {item.name}
                      </h4>
                      <Badge variant="outline" className="border-honey-300 text-honey-900 mb-2">
                        {item.selectedVariant.size}
                      </Badge>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center border border-honey-200 rounded-md">
                          <button
                            onClick={() => updateQuantity(item.cartItemId, -1)}
                            className="p-1 hover:bg-honey-50 transition-colors"
                          >
                            <Minus className="w-4 h-4 text-honey-900" />
                          </button>
                          <span className="px-3 text-sm font-medium text-honey-950">
                            {item.quantity}
                          </span>
                          <button
                            onClick={() => updateQuantity(item.cartItemId, 1)}
                            className="p-1 hover:bg-honey-50 transition-colors"
                          >
                            <Plus className="w-4 h-4 text-honey-900" />
                          </button>
                        </div>
                        <button
                          onClick={() => removeItem(item.cartItemId)}
                          className="p-1 hover:bg-red-50 rounded transition-colors"
                        >
                          <Trash2 className="w-4 h-4 text-red-600" />
                        </button>
                      </div>
                      <p className="text-sm font-bold text-honey-900 mt-2">
                        ${item.selectedVariant.price * item.quantity} MXN
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>

            <div className="border-t border-honey-100 pt-4 space-y-4">
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-honey-900/70">Subtotal</span>
                  <span className="font-medium text-honey-950">${subtotal} MXN</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-honey-900/70">Envío</span>
                  <span className="font-medium text-honey-950">${shipping} MXN</span>
                </div>
                <Separator className="bg-honey-200" />
                <div className="flex justify-between text-lg font-bold">
                  <span className="text-honey-950">Total</span>
                  <span className="text-honey-900">${total} MXN</span>
                </div>
              </div>

              <div className="space-y-2">
                <Button
                  onClick={handleCheckout}
                  className="w-full bg-honey-600 hover:bg-honey-700 text-white font-semibold py-6 transition-colors"
                >
                  Proceder al pago
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
                <Button
                  onClick={clearCart}
                  variant="outline"
                  className="w-full border-honey-300 text-honey-900 hover:bg-honey-50"
                >
                  Vaciar carrito
                </Button>
              </div>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};
